import * as React from "react";
import { useNavigate } from "@opendash/router";
import styled from "styled-components";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  Card,
  InfoBox,
  ImageContainer,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

// Asset Imports
const privathart1 = require("../../assets/privathart1.png");
const privathart2 = require("../../assets/privathart2.png");
const privathart3 = require("../../assets/privathart3.png");

// Route-specific styled component
const ComparePoint = styled.div`
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-left: 4px solid #ff9800;
  padding: 15px 20px;
  margin: 15px 0;
  border-radius: 0 8px 8px 0;

  p {
    margin-bottom: 0;
    font-weight: 500;
  }
`;

export const PrivathaushaltHaerteRoute = React.memo(
  function PrivathaushaltHaerteRoute() {
    const navigate = useNavigate();
    const { t } = useTexts();

    const tabs = [
      {
        title: t("privathaushalt.tab.1.title", "Wasserverbrauch & Spartipps"),
        path: "/wasserklaerer/privathaushalt/verbrauch",
        isActive: false,
      },
      {
        title: t("privathaushalt.tab.2.title", "Der Wasserpreis"),
        path: "/wasserklaerer/privathaushalt/preis",
        isActive: false,
      },
      {
        title: t("privathaushalt.tab.3.title", "Härtegrad des Wassers"),
        path: "/wasserklaerer/privathaushalt/haerte",
        isActive: true,
      },
    ];

    return (
      <AppLayout>
        <ContentContainer>
          <h1>{t("privathaushalt.haerte.title", "Härtegrad des Wassers")}</h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          <Card>
            <p>
              {t(
                "privathaushalt.haerte.intro",
                "Eine solche Grafik auf Ihrer Waschmittelverpackung haben Sie womöglich schon einmal gesehen. Doch was ist damit gemeint?"
              )}
            </p>
            <ImageContainer>
              <img src={privathart1} alt="Waschmittel-Dosierung" />
              <FigureCaption>
                {t(
                  "privathaushalt.haerte.image1.caption",
                  "Abbildung 1: Waschmittel-Dosierung [eigene Darstellung aus [1]]"
                )}
              </FigureCaption>
            </ImageContainer>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.haerte.section1.title",
                "Was ist die Wasserhärte?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.haerte.section1.text1",
                  "Im Wesentlichen beschreibt der Härtegrad des Wassers den Anteil der Mineralien Calcium und Magnesium im Wasser. Hierbei werden drei Stufen unterschieden: Der Härtegrad von Wasser kann weich, mittel oder hart sein. [1]"
                )
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.haerte.section2.title",
                "Warum gibt es verschiedene Härtegrade?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.haerte.section2.text1",
                  "Sobald Regenwasser auf den Boden trifft, versickert es nach und nach bis zum Grundwasser. Auf seinem Weg durch den Boden nimmt es Calcium und Magnesium aus dem Untergrund auf. Die Menge der gelösten Mineralien variiert in Abhängigkeit von der Art des Gesteins. Aus diesem Grund weist Grundwasser von Region zu Region unterschiedliche Härtegrade auf und aus demselben Grund stammt weiches Wasser meist auch aus Talsperren und Seen. Ist das bezogene Wasser zu hart, gibt es eine Enthärtungsstufe in einigen Wasserwerken. [1]"
                )
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.haerte.section3.title",
                "Wie machen sich Unterschiede in den Härtegraden bemerkbar?"
              )}
            </h2>

            <ImageContainer>
              <img src={privathart2} alt="Mineralien im Wasser" />
              <FigureCaption>
                {t(
                  "privathaushalt.haerte.image2.caption",
                  "Abbildung 2: Mineralien im Wasser [eigene Darstellung aus [1]]"
                )}
              </FigureCaption>
            </ImageContainer>

            <ComparePoint>
              <p>
                →{" "}
                {processInlineFormatting(
                  t(
                    "privathaushalt.haerte.section3.point1",
                    "Weiches Wasser schmeckt neutraler und hinterlässt keine Rückstände [1]"
                  )
                )}
              </p>
            </ComparePoint>

            <ComparePoint>
              <p>
                →{" "}
                {processInlineFormatting(
                  t(
                    "privathaushalt.haerte.section3.point2",
                    "Hartes Wasser mit einem höheren Mineraliengehalt schmeckt markanter und führt zu stärkeren Kalkablagerungen. Diese lassen sich jedoch umweltfreundlich einfach mit Haushaltsessig oder Zitronensäure entfernen [1]"
                  )
                )}
              </p>
            </ComparePoint>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.haerte.section4.title",
                "Ist hartes Wasser ungesund für uns Menschen?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.haerte.section4.text1",
                  'Nein. Egal welche Härte das Wasser besitzt, in Deutschland besteht eine einwandfreie Trinkwasserqualität. Dem Menschen kann es aus gesundheitlicher Sicht egal sein, ob er weiches, mittleres oder hartes Wasser trinkt. Arterien "verkalken" auch nicht wie die Armaturen im Bad oder in der Küche. Das ist ein Mythos. Für den menschlichen Körper sind Calcium und Magnesium sogar wichtige Mineralien. Daher ist eine Wasserenthärtungsanlage für das Zuhause zumindest aus gesundheitlicher Perspektive nicht notwendig. [1]'
                )
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.haerte.section5.title",
                "Wie hart ist mein Trinkwasser?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.haerte.section5.text",
                  "Um herauszufinden, wie hart das Trinkwasser in Ihrer Region ist, fragen Sie am besten bei Ihrem lokalen Wasserversorger oder Ihrer Stadtverwaltung nach. [1]"
                )
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.haerte.section6.title",
                "Warum ist es sinnvoll meinen Härtegrad zu kennen?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.haerte.section6.text1",
                  "Wann der Härtegrad des Wassers eine Rolle spielt, ist beispielsweise bei der richtigen Einstellung Ihrer Geschirrspülmaschine oder bei der richtigen Dosierung Ihres Waschmittels für die Waschmaschine. [1]"
                )
              )}
            </p>

            <ImageContainer>
              <img src={privathart3} alt="Waschmittel-Dosierung" />
              <FigureCaption>
                {t(
                  "privathaushalt.haerte.image3.caption",
                  "Abbildung 3: Waschmittel-Dosierung [eigene Darstellung aus [1]]"
                )}
              </FigureCaption>
            </ImageContainer>

            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.haerte.section6.text2",
                  "Wann es ebenfalls sinnvoll sein kann den eigenen Härtegrad des Leitungswassers zu kennen, ist bei der Gartenbewässerung. Für Pflanzen ist weiches Wasser ideal. [2] Sie müssen für die Bewässerung Ihres Gartens aber nicht gleich eine Enthärtungsanlage kaufen. Eine Lösung ist das weiche Regenwasser zur Bewässerung zu nutzen."
                )
              )}
            </p>
          </Card>

          <SourcesSection>
            <h3>
              {t(
                "privathaushalt.haerte.literatur.title",
                "Literaturverzeichnis"
              )}
            </h3>
            <ol>
              <li id="quelle-1">
                <LinkifyText>
                  {t(
                    "privathaushalt.haerte.literatur.ref1",
                    '[1] Umweltbundesamt (Hrsg.), "Hartes Wasser, weiches Wasser", Umweltbundesamt, 2016. Zugegriffen: 15. Dezember 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/themen/hartes-wasser-weiches-wasser'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-2">
                <LinkifyText>
                  {t(
                    "privathaushalt.haerte.literatur.ref2",
                    '[2] Umweltbundesamt (Hrsg.), "Tipps für eine nachhaltige Regenwassernutzung", Umweltbundesamt, 2025. Zugegriffen: 15. Dezember 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/umwelttipps-fuer-den-alltag/garten-freizeit/regenwassernutzung'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
